<template>
  <div class="ambassadors-page">
    <div class="main-info">
      <img class="header" :src="require('@/assets/ambassadors/ambassador-page-header.svg')"/>
      <div class="content">
        <div class="info">
          <span class="text bold">
            אנחנו מזמינים אותך לקחת חלק בתוכנית השגרירים שלנו, להמליץ עלינו לאחרים ולהרוויח פעמיים:
          </span>
          <span class="text">
            עד 150 ש"ח מתנה בכל המלצה וגם תחושה נהדרת מזה שעזרת לאחרים לחיות טוב יותר עם הכסף שלהם.
          </span>
          <div class="approval-text">
            <span class="text small">שיתוף הקישור מהווה הסכמה </span>
            <a class="text small" href="https://www.riseup.co.il/ambassadorsregulations" target="_blank">לתנאי התקנון</a>
          </div>
        </div>
        <div v-if="loading" class="share-buttons">
          <loader class="loading-link" variant="warning"/>
          <span class="text small">מייצרים לך לינק אישי</span>
        </div>
        <div v-else-if="hasCreationError" class="share-buttons">
          <span class="text small error">המערכת לא הצליחה לייצר קישור, אפשר לנסות שוב מאוחר יותר</span>
        </div>
        <div class="share-buttons" v-else>
          <riseup-button title="שיתוף עם חברים" size="slim" :action="shareClick"/>
          <span class="text copy-text">אפשר גם להעתיק לינק</span>
          <input-copy class="input-copy" :input-content="link" @copied="linkCopied"/>
        </div>
      </div>
    </div>
    <div class="steps-section">
      <h1 class="steps-header bold">איך זה עובד בדיוק?</h1>
      <div class="steps">
        <step description="מזמינים חבר או חברה להצטרף בעזרת הלינק למעלה" number="1"/>
        <step
          description="אם החבר או החברה מתחילים את תקופת הנסיון ברייזאפ אתם מקבלים 50 ש”ח"
          number="2"
        />
        <step description="אם הם נשארים ועושים מנוי אתם מקבלים עוד 100 ש״ח" number="3"/>
      </div>
      <div class="regulations">
        <span class="text small">התשלום יעשה בתוך 30 יום ובכפוף למסירת תעודת זהות</span>
        <div class="regulation-link">
          <a class="steps-regulations text" href="https://www.riseup.co.il/ambassadorsregulations" target="_blank">לתקנון
            המלא</a>
          <img class='image' :src="require('@/assets/regulations-arrow.svg')">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SumsumApi from '@/api/SumsumApi';
import BaseUI from 'base-ui';
import Segment from '@/Segment';
import ShareUtils from '@/utils/share';
import ShareEvents from '@/utils/shareEvents';
import EventBus from '@/event-bus/event-bus';
import TextCopiedToaster
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/toasters/TextCopiedToaster';
import TextCopyFailedToaster
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/toasters/TextCopyFailedToaster';
import { mapActions, mapState } from 'vuex';
import AmbassadorIdPopUp from '@/pages/responsive-pages/authenticated/navigation-pages/ambassadors/AmbassadorIdPopUp';
import DDLogs from '@/DDLogs';
import Step from './Step';

export default {
  name: 'AmbassadorsPage',
  components: {
    RiseupButton: BaseUI.RiseupButton,
    InputCopy: BaseUI.InputCopy,
    Loader: BaseUI.TinyLoader,
    Step,
  },
  props: {
    showIdPopup: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState('session', ['activeMember']),
  },
  mounted() {
    EventBus.$emit('reset-scroll');
  },
  data() {
    return {
      link: '',
      loading: false,
      hasCreationError: false,
    };
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal']),
    async shareClick() {
      const segmentData = {
        icrc: this.link.split('icrc=')[1],
        shareName: 'ambassadorLink',
      };

      ShareEvents.trackShareIntentClicked(segmentData);
      await ShareUtils.shareLink(
        this.link,
        'הי, נראה לי שהשירות של רייזאפ יכול להתאים לך, החודשיים הראשונים חינם, רוצה לנסות?',
        segmentData,
      );
    },
    linkCopied() {
      Segment.trackUserInteraction('JoinAmbassadorPage_linkCopied');
      EventBus.$emit('open-toaster', {
        component: TextCopiedToaster,
        props: {},
      });
    },
    async getLink() {
      this.loading = true;
      try {
        this.link = await SumsumApi.getOrCreateAmbassadorLinkWithoutErrorHandling();
      } catch (error) {
        DDLogs.error('ambassador get or create link failed', { error });
        this.hasCreationError = true;
      } finally {
        this.loading = false;
      }
    },
    copyFailed() {
      EventBus.$emit('open-toaster', {
        component: TextCopyFailedToaster,
        props: {},
      });
    },
  },
  created() {
    Segment.trackUserGot('JoinAmbassadorPage_shown');

    this.getLink();

    if (this.showIdPopup && !this.activeMember.idNumberPiiId) {
      // Open id modal when member doesn't have an id in db
      this.openModal({
        component: AmbassadorIdPopUp,
      });
    }
  },
};
</script>

<style scoped lang="scss">
@import "~base-ui/src/scss/riseup-colors";

.ambassadors-page {
  display: flex;
  flex-direction: column;

  a {
    color: $riseup_blue;
  }

  .text {
    font-size: 18px;
    line-height: 140%;

    &.small {
      font-size: 14px;
    }

    &.error {
      color: $riseup_danger_red;
    }
  }

  .bold {
    font-weight: bold;
  }

  .main-info {
    background-color: $riseup_yellow;

    .header {
      width: 100%;
    }

    .content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
      padding: 0 24px 55px 24px;

      .info {
        margin: 40px 0;
        text-align: center;

        .approval-text {
          margin-top: 24px;
        }
      }

      .share-buttons {
        height: 182px;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .text {
          text-align: center;
        }

        .loading-link {
          margin-bottom: 24px;
        }

        .input-copy {
          width: 100%;
          display: flex;
          flex-direction: row-reverse;
        }
      }

      .copy-text {
        margin: 24px 0;
      }
    }
  }

  .steps-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 0 62px 0;

    .steps-header {
      font-family: PauzaBold;
      font-size: 34px;
      line-height: 140%;
    }

    .steps {
      display: flex;
      flex-direction: column;
      gap: 48px;
      margin-top: 24px;
      margin-bottom: 87px;
    }

    .regulations {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      margin: 0 99px;
      gap: 13px;

      .regulation-link {
        display: flex;
        align-items: center;
        gap: 3px;

        .steps-regulations {
          text-decoration: none;
          font-weight: bold;
        }
      }
    }
  }
}
</style>
